import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, GetColorStatus, capitalizeFirstLetterOfWords, withStyles } from 'vopay';
import { UtilButton, TransactionStatus } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useElinx from '@/components/Elinx/ElinxHistory/Details/useElinx';
import styles from '@/components/UI/TransactionCard/TransactionCard.module.scss';

const cn = withStyles(styles);

type UseElinxDetailsParams = {
  ELinxRequestID: string;
  jwt: string;
};

export default function useElinxDetails({ ELinxRequestID, jwt }: UseElinxDetailsParams) {
  const toast = useToast();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ElinxTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { details, documentLink, documentName, fetchElinxDetails, fetchDocument } = useElinx({ bearer: jwt });

  useEffect(() => {
    if (!ELinxRequestID) return;
    fetchElinxDetails({ ELinxRequestID }).catch(() => {
      toast.neutral(t('ErrorMessages.fetchingError', { defaultValue: 'Failed to fetch eLinx details' }));
    });
  }, [ELinxRequestID]);

  useEffect(() => {
    if (!details?.DocumentID) return;
    fetchDocument({ DocumentID: details.DocumentID }).catch(() => {
      toast.neutral(t('ErrorMessages.fetchingDocumentError', { defaultValue: 'Failed to fetch the invoice' }));
    });
  }, [details?.DocumentID]);

  const elinxAdditionalId = ELinxRequestID ? (
    <Box flx="col" gapSize="6px" ai="fs">
      <div>{t('TransactionDetails.elinxId', { defaultValue: 'eLinx ID' })}</div>
      <UtilButton
        className={cn('idButton')}
        onClick={() => window.open(`/elinx-history/details/${ELinxRequestID}`, '_blank')}
      >
        {ELinxRequestID}
      </UtilButton>
      {details?.Status && (
        <TransactionStatus
          status={GetColorStatus(details.Status)}
          label={tGlobal(`Transaction.TransactionHistory.statuses.${details.Status.toLowerCase()}`, {
            defaultValue: capitalizeFirstLetterOfWords(details.Status),
          })}
        />
      )}
    </Box>
  ) : undefined;

  const invoiceButton = documentLink ? (
    <UtilButton
      size="medium"
      iconRightProps={{ icon: 'visibility', fillAmount: 1 }}
      onClick={() => window.open(documentLink, '_blank')}
    >
      {documentName || t('TransactionDetails.invoice', { defaultValue: 'Invoice' })}
    </UtilButton>
  ) : undefined;

  return { details, documentLink, documentName, elinxAdditionalId, invoiceButton };
}
